import { formatMoney } from '@fdt/shared';
import { Typography, type TypographyProps } from '@mui/material';
import { currentLocale } from '../i18n';

interface MoneyTextProps extends Omit<TypographyProps, 'children'> {
  value: string;
  /** Colours the figure by sign: red when owed, green when in credit. */
  signed?: boolean;
  /** Shows a leading plus on positive values, for ledger-style columns. */
  showPlus?: boolean;
}

/**
 * A monetary figure, formatted for the active locale and aligned for reading
 * down a column.
 *
 * Tabular numerals matter here: proportional digits make a column of amounts
 * impossible to scan, which is most of what a statement is for.
 */
export function MoneyText({ value, signed, showPlus, sx, ...props }: MoneyTextProps) {
  const locale = currentLocale();
  const amount = Number.parseFloat(value);
  const formatted = formatMoney(value, locale);

  return (
    <Typography
      component="span"
      sx={{
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
        ...(signed && amount !== 0
          ? { color: amount > 0 ? 'error.main' : 'success.main', fontWeight: 500 }
          : {}),
        ...sx,
      }}
      {...props}
    >
      {showPlus && amount > 0 ? '+' : ''}
      {formatted}
    </Typography>
  );
}
